export interface HeroMediaSelection<THero, TDetail, TEpisode, TPlay> {
  hero: THero
  dramaId: string
  detail: TDetail
  episode: TEpisode
  play: TPlay
}

export interface HeroMediaCoordinatorOptions<THero, TDetail, TEpisode extends { id: number }, TPlay> {
  dramaId: (hero: THero) => string
  detail: (dramaId: string) => Promise<TDetail>
  firstEpisode: (detail: TDetail) => TEpisode | undefined
  play: (episodeId: number) => Promise<TPlay>
  commit: (selection: HeroMediaSelection<THero, TDetail, TEpisode, TPlay>) => void
  clear: () => void
}

export function createHeroMediaCoordinator<THero, TDetail, TEpisode extends { id: number }, TPlay>(
  options: HeroMediaCoordinatorOptions<THero, TDetail, TEpisode, TPlay>,
) {
  let activeEpoch = 0
  let loading = false

  return {
    async select(hero: THero) {
      const requestEpoch = ++activeEpoch
      loading = true
      const dramaId = options.dramaId(hero)
      if (!dramaId) {
        if (requestEpoch === activeEpoch) loading = false
        return false
      }
      try {
        const detail = await options.detail(dramaId)
        if (requestEpoch !== activeEpoch) return false
        const episode = options.firstEpisode(detail)
        if (!episode) return false
        const play = await options.play(episode.id)
        if (requestEpoch !== activeEpoch) return false
        options.commit({ hero, dramaId, detail, episode, play })
        return true
      } catch {
        return false
      } finally {
        if (requestEpoch === activeEpoch) loading = false
      }
    },
    canAutoAdvance() {
      return !loading
    },
    invalidate() {
      activeEpoch += 1
      loading = false
      options.clear()
    },
  }
}
